import {noop} from '@benchling/js-utils';

import BenchlingConfig from 'BenchlingConfig';
import {
  computeCanMaximize,
  computeCanSplitScreen,
  computeIsFixedHeight,
  computeIsFullHeight,
  computeIsMaximizedOrSplitScreen,
} from 'notebook/tables/store/logic/TableViewsLogic';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';

import {TableContextModel} from './TableContextSelectors';

export interface TableViewsModel {
  isFixedHeight: boolean;
  isFullHeight: boolean;
  isMaximizedOrSplitScreen: boolean;
  canMaximize: boolean;
  canSplitScreen: boolean;
}

export function createTableViewsSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  tableContextSelector: (store: TableEditableStore) => TableContextModel
): (store: TableEditableStore) => TableViewsModel {
  return createTableStoreSelector(
    'tableViews',
    {
      isMaximized: (store) => store.stableProps.isMaximized ?? false,
      isSplitScreen: (store) => store.stableProps.isSplitScreen ?? false,
      isFocused: (store) => store.stableProps.isFocused ?? false,
      maximumHeight: (store) => store.stableProps.maximumHeight,
      enableTableFixedHeightAsDefault: () => BenchlingConfig.ENABLE_TABLE_FIXED_HEIGHT_AS_DEFAULT,
      disableMaximizedTables: (store) => store.stableProps.disableMaximizedTables ?? false,
      setSplitScreenTable: (store) => store.stableProps.setSplitScreenTable,
      tableProvenanceUuid: (store) => tableContextSelector(store).tableProvenanceUuid,
    },
    ({
      isMaximized,
      isSplitScreen,
      isFocused,
      maximumHeight,
      enableTableFixedHeightAsDefault,
      disableMaximizedTables,
      setSplitScreenTable,
      tableProvenanceUuid,
    }) => {
      const isMaximizedOrSplitScreen = computeIsMaximizedOrSplitScreen(isMaximized, isSplitScreen, isFocused);
      const isFixedHeight = computeIsFixedHeight(
        isMaximizedOrSplitScreen,
        maximumHeight != null,
        enableTableFixedHeightAsDefault
      );
      return {
        isMaximizedOrSplitScreen,
        isFixedHeight,
        isFullHeight: computeIsFullHeight(isFixedHeight, isMaximizedOrSplitScreen),
        canMaximize: computeCanMaximize(disableMaximizedTables, tableProvenanceUuid),
        canSplitScreen: computeCanSplitScreen(
          setSplitScreenTable,
          setSplitScreenTable === noop,
          tableProvenanceUuid
        ),
      };
    }
  );
}
